import React from "react";
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
    Search
} from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import { useSmoothLoading } from "lib/use-smooth-loading";
import { BranchwiseUnbilledRow } from "../api/api";


interface Props {
    data: BranchwiseUnbilledRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: keyof BranchwiseUnbilledRow;
    sortOrder: "asc" | "desc";
    search: string;
    onSearchChange: (val: string) => void;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (type: 'INR' | 'Lakhs' | 'Crores') => void;
    onSortChange: (col: keyof BranchwiseUnbilledRow) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onBack?: () => void;
    onBranchClick: (ouCode: string, branchName: string) => void;
    /** When omitted, the "Customer: …" heading is hidden (e.g. key-accounts detail). */
    customerName?: string;
}

const columns: Array<{ key: keyof BranchwiseUnbilledRow; label: string; isNumeric?: boolean }> = [
    { key: "Branch", label: "Branch" },
    { key: "TotalUnbilled", label: "Total Unbilled", isNumeric: true },
    { key: "InTransit", label: "In Transit", isNumeric: true },
    { key: "InStock", label: "In Stock", isNumeric: true },
    { key: "DeliveredLessThanEqual10", label: "Delivered <= 10", isNumeric: true },
    { key: "DeliveredGreaterThan10", label: "Delivered > 10", isNumeric: true },
    { key: "PodReceivedLessThanEqual10", label: "POD Received <= 10", isNumeric: true },
    { key: "PodReceivedGreaterThan10", label: "POD Received > 10", isNumeric: true },
];

const BranchwiseUnbilledTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    search,
    onSearchChange,
    conversionType,
    onConversionChange,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    onBack,
    onBranchClick,
    customerName
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const { isLoading } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const formatNumber = (value: number): string => {
        let displayValue = value;
        switch (conversionType) {
            case 'Lakhs':
                displayValue = value / 100000;
                break;
            case 'Crores':
                displayValue = value / 10000000;
                break;
        }

        return displayValue.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const renderSkeletonRows = () =>
        Array.from({ length: 12 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));

    return (
        <div className="flex flex-col h-full">
            <div className="shrink-0 space-y-4 pb-4">
                {(onBack || customerName) && (
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                            >
                                ← Back
                            </button>
                        )}

                        {customerName && (
                            <h2 className="text-lg font-semibold">
                                Customer: {customerName}
                            </h2>
                        )}
                    </div>
                )}

                <div className="flex items-end justify-between gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Search Branch Name</label>

                        <div className="relative w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder="Search by branch name..."
                                className="w-full h-10 rounded-md border pl-9 pr-3 text-sm"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2">
                        {(['INR', 'Lakhs', 'Crores'] as const).map((t) => (
                            <button
                                key={t}
                                onClick={() => onConversionChange(t)}
                                className={`px-3 py-1 text-sm rounded-md ${conversionType === t
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                    }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="border border-gray-200 overflow-auto flex-1">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const active = sortBy === col.key;
                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.key)}
                                        className={`border border-gray-300 px-2 py-2 cursor-pointer whitespace-nowrap font-semibold
                                            ${col.isNumeric ? "text-right" : "text-left"}`}
                                    >
                                        <div className="flex items-center gap-1">
                                            {col.label}
                                            {active ? (
                                                sortOrder === "asc"
                                                    ? <ChevronUp size={14} />
                                                    : <ChevronDown size={14} />
                                            ) : (
                                                <ChevronUp size={14} className="text-gray-300" />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            renderSkeletonRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-16 text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => {
                                const isTotal = idx === 0;
                                return (
                                    <tr
                                        key={idx}
                                        onClick={() => {
                                            if (!isTotal && row.ou_code) {
                                                onBranchClick(row.ou_code, row.Branch);
                                            }
                                        }}
                                        className={`hover:bg-gray-50 ${isTotal ? "bg-blue-50 font-semibold" : ""}`}
                                    >
                                        {columns.map((col) => (
                                            <td
                                                key={col.key}
                                                className={`border border-gray-200 px-2 py-1.5 whitespace-nowrap
                                                ${col.isNumeric ? "text-right" : "text-left"}`}
                                            >
                                                {typeof row[col.key] === "number"
                                                    ? formatNumber(row[col.key] as number)
                                                    : row[col.key]}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-gray-600">
                    Showing <strong>{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</strong> to{" "}
                    <strong>{Math.min(page * pageSize, totalItems)}</strong> of{" "}
                    <strong>{totalItems}</strong>
                </span>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                        Rows per page
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="px-2 text-sm">
                            Page <strong>{page}</strong> / {totalPages}
                        </span>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronRight size={16} />
                        </button>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BranchwiseUnbilledTable;